import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CrossMarkAlt from 'components/Icons/CrossMarkAlt';
import Info from 'components/Icons/Info';
import PlusAlt from 'components/Icons/PlusAlt';
import UICreatePDSelect, {
  NewPDValue
} from 'components/Others/UICreatePDSelect';
import UIFileBadge from 'components/Others/UIFileBadge';
import UIPDSelect, { PDOption } from 'components/Others/UIPDSelect';
import UITooltip from 'components/Others/UITooltip';
import prescriptionService from 'services/prescription.service';
import { notifications } from '../../constants/notificationMessages';
import { CREATE_PRESCRIPTION_AND_PD } from '../../gqlStrings/mutations/Prescription';
import { setShowModal, setUseThisPD } from 'redux/features/pd/pdSlice';
import { addDataLayer } from '../../lib/ga';
import {
  setOpenPrescriptionGuestForm,
  setPrescriptions,
  setSelectedPrescription,
  setSelectBoxPrescription,
  setUsePrescriptionButton,
  setOpenPrescriptionSelection
} from '../../redux/features/prescription/prescriptionSlice';
import { RootState } from '../../redux/store';
import Button from '../Button/Button';
import Spinner from '../Spinner/Spinner';
import PdRangeTooltipMessage from './PdRangeTooltipMessage';
import PrescriptionUploadDropzone from './PrescriptionUploadDropzone';
import { useGlassesStore } from 'zus/glassesStore';
import { useProductStore } from 'zus/productStore';
import cx from 'classnames';
import useWindowSize from 'hooks/useWindowSize';
import { useRouter } from 'next/router';
export default function PrescriptionUploadForm({
  setPrescriptionUploadForm,
  setHome,
  onVisibleStepChanged,
  setIsFileSelected,
  cancelFileSelected
}: {
  setPrescriptionUploadForm?: React.Dispatch<React.SetStateAction<any>>;
  setHome?: React.Dispatch<React.SetStateAction<boolean>>;
  // eslint-disable-next-line no-unused-vars
  onVisibleStepChanged?: (value: 'UPLOAD' | 'FORM') => void;
  profile?: any;
  setIsFileSelected?: any;
  cancelFileSelected?: any;
}) {
  const selectEditedPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectEditedPrescription
  );
  const frameSize = useGlassesStore(state => state.frame_size);
  
  const armSize = useSelector(
    (state: RootState) => state.prescriptionEvent.armSize
  );
  const armsSize = useGlassesStore((state) => state.arms_size)
  const usePDMeter = useSelector((state: any) => state.pdEvent.setPD)
  const calculatedPd = useSelector((state: any) => state.pdEvent.calculatedPd)
  const setPriscriptionFormValues = useProductStore((state:any) => state.setPriscriptionFormValues)
  const setActiveSelection = useProductStore((state:any) => state.setActiveSelection)
  const setActiveSelectionIdx = useProductStore((state:any) => state.setActiveSelectionIdx)
  const [selectedPdId, setSelectedPdId] = useState('');

  const uploadFormFileSelected = useProductStore((state:any) => state.uploadFormFileSelected)
  const setUploadFormFileSelected = useProductStore((state:any) => state.setUploadFormFileSelected)
  
  const setUploadFormOpen = useProductStore((state:any) => state.setUploadFormOpen)
  const { register, handleSubmit } = useForm({
    mode: 'onChange',
    defaultValues: {
      note: (selectEditedPrescription && selectEditedPrescription.note) || ''
    }
  });
  const onError = () => {
    addDataLayer({ event: 'prescription_fail' }, false);
  };

  const selectedPrescription = useSelector(
    (state: RootState) => state.prescriptionEvent.selectedPrescription
  );

  const isLogin = useSelector((state: RootState) => state.authEvent.isLogin);

  const prescriptions = useSelector(
    (state: RootState) => state.prescriptionEvent.prescriptions
  );

  const [pdSelectionType, setPdSelectionType] = useState<'SELECT' | 'CREATE'>(
    'SELECT'
  );

  const [pdList, setPdList] = useState<PDOption[]>([]);

  const { width } = useWindowSize();
  const MOBILE_VIEWPORT_WIDTH = 1023;
	const router = useRouter();
	const isMobile = useMemo(
		() => width <= MOBILE_VIEWPORT_WIDTH,
		[width]
	);

  const loadPdList = async () => {
    if (!isLogin) {
      setPdSelectionType('CREATE');
      return;
    }
    setCreatePrescriptionLoading(true);
    const savedPdList = await prescriptionService.findAllPds(
      frameSize as string
    );
    setPdList(savedPdList);
    if (savedPdList.length === 1) {
      setSelectedPdId(savedPdList[0].id);
    }
    setCreatePrescriptionLoading(false);
  };

  useEffect(() => {
    loadPdList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPdTypeError = (data: any) => {
    const isEdit = !!selectEditedPrescription;
    const pdR = Number(data.pdR);
    const pdL = Number(data.pdL);
    const pd = Number(data.pd);

    const isFailed = pdR && pdL && pd && pdR + pdL !== pd;

    if (isEdit && isFailed) {
      toast.custom((t) => (
        <div
          onClick={() => toast.dismiss(t.id)}
          className={`${t.visible ? 'animate-enter' : 'animate-leave'
            } flex w-[320px] cursor-default select-none items-center justify-center rounded bg-brand-red p-4 text-center text-sm font-medium text-white shadow-lg`}>
          {notifications.PRES_PD_TYPE_ERROR}
        </div>
      ));
      return true;
    }

    return false;
  };

  const [file, setFile] = useState<File>();
  const [buttonSticky, setButtonSticky] = useState(true);

  // eslint-disable-next-line no-unused-vars
  const [selected, setSelected] = useState(selectedPrescription);
  const [createPrescriptionLoading, setCreatePrescriptionLoading] =
    useState(false);

  const [newPd, setNewPd] = useState<NewPDValue>({
    type: 'bino',
    left: '',
    right: '',
    pd: ''
  });

  const dispatch = useDispatch();

  useEffect(() => {
    setSelected(selectedPrescription);
  }, [selectedPrescription]);

  const showFileUploadSection = useMemo(() => {
    if (!selectEditedPrescription) return !file;
    return !selectEditedPrescription?.files?.length;
  }, [file, selectEditedPrescription]);

  const deleteAlert = withReactContent(Swal);

  useEffect(() => {
    onVisibleStepChanged?.(showFileUploadSection ? 'UPLOAD' : 'FORM');
  }, [showFileUploadSection, onVisibleStepChanged]);

  useEffect(() => {
    if (usePDMeter) {
      let calculatedSize = (calculatedPd / 2) % 0.5 != 0 ? ((calculatedPd / 2) + 0.25) : (calculatedPd / 2);
      if (newPd.type == 'mono') {
        setNewPd(Object.assign({}, newPd, { "left": calculatedSize + '', "right": calculatedSize + '' }))
      } else {
        setNewPd(Object.assign({}, newPd, { "pd": calculatedPd }))
      }

      localStorage.setItem('calculatedPd', JSON.stringify({ 'pd': calculatedPd, 'monoPD': calculatedSize + '' }))
      dispatch(setUseThisPD(false))
    }
  }, [usePDMeter])


  const askRemoveFile = async () => {
    const { isConfirmed } = await deleteAlert.fire({
      width: 300,
      customClass:{
        container: 'z-[999999]'
      },
      html: (
        <div>
          <p className="text-base font-medium">
            Are you sure want to remove selected file, your changes will be
            deleted?
          </p>
          <div className="mt-4 flex w-full items-center justify-center">
            <button
              className="mr-3 rounded bg-brand-black50 px-5 py-2 text-sm text-white"
              onClick={() => {
                deleteAlert.clickConfirm();
              }}>
              Yes, delete
            </button>
            <button
              className="rounded border border-brand-grey175 bg-white px-5 py-2 text-sm text-brand-black50"
              onClick={() => deleteAlert.close()}>
              Cancel
            </button>
          </div>
        </div>
      ),
      showConfirmButton: false
    });
    if (!isConfirmed) return;
    setFile(undefined);
    setUploadFormFileSelected(false);
    
  };

  const [mutatePrescriptionFunction] = useMutation(CREATE_PRESCRIPTION_AND_PD, {
    context: {
      clientName: 'laravel',
      upload: true
    },
    onCompleted: (getUserData) => {
      addDataLayer({ event: 'prescription_success' }, false);
      if (isLogin) {
        const NewPrescription = Object.assign([], prescriptions);
        NewPrescription.unshift(getUserData.createPrescriptionAndPd);
        dispatch(setPrescriptions(NewPrescription));

        if (setHome) {
          setHome(true);
        }
        if (setPrescriptionUploadForm) {
          setPrescriptionUploadForm(false);
        }
      }

      dispatch(setSelectedPrescription(getUserData.createPrescriptionAndPd));
      dispatch(setSelectBoxPrescription(getUserData.createPrescriptionAndPd));
      dispatch(setUsePrescriptionButton(true));
      setCreatePrescriptionLoading(false);

      setActiveSelection('lens')
      setActiveSelectionIdx(3)

      

      if (!isLogin) {
        document
          .getElementsByTagName('body')[0]
          .classList.remove('overflow-hidden-body');

        dispatch(setOpenPrescriptionGuestForm(false));
      }
      // toast.custom((t) => (
      //   <div
      //     onClick={() => toast.dismiss(t.id)}
      //     className={`${t.visible ? 'animate-enter' : 'animate-leave'
      //       } flex w-[300px] cursor-default select-none items-center justify-center rounded bg-brand-green p-4 text-center text-sm font-medium text-white shadow-lg`}>
      //     {notifications.CREATE_PRESCRIPTION}
      //   </div>
      // ));
      dispatch(setOpenPrescriptionGuestForm(false));
      dispatch(setOpenPrescriptionSelection(false));
    },
    onError: (error) => {
      addDataLayer({ event: 'prescription_fail' }, false);
      setCreatePrescriptionLoading(false);
      toast.custom((t) => (
        <div
          onClick={() => toast.dismiss(t.id)}
          className={`${t.visible ? 'animate-enter' : 'animate-leave'
            } flex w-[300px] cursor-default select-none items-center justify-center rounded bg-brand-red p-4 text-center text-sm font-medium text-white shadow-lg`}>
          {error.message}
        </div>
      ));
    }
  });

  function onPrescriptionSubmit(data: any) {
    if (checkPdTypeError(data)) {
      return;
    }
    setActiveSelection("pd");
    setActiveSelectionIdx(2);
    
    setPriscriptionFormValues({...data, file, arm_size:armsSize, frame_size:frameSize, fromUpload:true})
    setUploadFormOpen(false)
    dispatch(setSelectedPrescription(null));
  }

  const onFileSelected = (file: File) => {
    setFile(file);
    if(file){
      setIsFileSelected?.(() => true);
      setUploadFormFileSelected(true);
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (createPrescriptionLoading) return true;

    if (pdSelectionType === 'CREATE') {
      return newPd.type === 'bino' ? !newPd.pd : !newPd.right || !newPd.left;
    }

    return !selectedPdId;
  }, [
    createPrescriptionLoading,
    newPd.left,
    newPd.pd,
    newPd.right,
    newPd.type,
    pdSelectionType,
    selectedPdId
  ]);

  return <>
    { (
      <>
        <span className={cx({'hidden':(uploadFormFileSelected)})}><PrescriptionUploadDropzone cancelFileSelected={cancelFileSelected} onFileSelected={onFileSelected} /></span>
        <span className={cx({'hidden':(!uploadFormFileSelected)})}> 
          <div className='text-center mb-5 text-xl font-bold'>Upload prescription</div>
          <div className='max-w-xs text-center mx-auto mb-6'>After your prescription is uploaded, please enter your PD number!</div>
          <form
            className="flex w-full flex-col overflow-y-auto px-5 md:h-full"
            onSubmit={handleSubmit(onPrescriptionSubmit, onError)}>
            {!!file && (
              <UIFileBadge
                onClick={() => window.open(URL.createObjectURL(file), '_blank')}
                fileName={file.name}
                title="Prescription file selected"
                onRemoveClick={() => askRemoveFile()}
              />
            )}
            <div className='mx-5'>
            <div className="my-8 h-[1px] w-full px-5 bg-brand-grey75 opacity-20"></div>
            <div className="flex-col">
              <div className="[w-full] w-[20%] mdx:mb-4">
                <label
                  htmlFor="addressname"
                  className="font-sans whitespace-nowrap text-sm w-full font-medium text-brand-black75">
                  <span className='font-bold'>Add Note</span> (optional)
                </label>
              </div>
              <input
                onFocus={() => {
                  setButtonSticky(false);
                }}
                {...register('note', {
                  onBlur: () => {
                    setButtonSticky(true);
                  }
                })}
                id="addressname"
                placeholder="Type your additional notes here..."
                className="focus:outline-none w-full mt-4 resize-none rounded border border-brand-grey350 bg-white p-3 text-[14px] font-normal text-black placeholder-brand-grey75 mdx:w-full"
              />
            </div>

            <Button
              type="submit"
              className={
                'sticky bottom-0 right-0 mt-5 w-full justify-center bg-brand-black100 text-center' +
                (buttonSticky && 'sticky')
              }
              textClass="font-medium text-white text-sm"
              >
              <div className="flex">
                {selectEditedPrescription
                  ? 'Update prescription'
                  : 'Add prescription'}
                {createPrescriptionLoading ? (
                  <div className="ml-2 mt-1">
                    <Spinner fill="white" />
                  </div>
                ) : null}
              </div>
            </Button>
          
          </div>
        </form>
        </span> 
      </>
    )}
  </>;
}
